import { useMemo, useState } from 'react';
import { isEqual } from 'lodash-es';
import { z } from '@customerio/hydra-validation';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { parseAsBoolean, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useDebounceCallback } from '@/hooks/state/use-debounce-callback';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { getObjectsQueryOptions } from '@/services/db/object/queries';
import { lzQueryDecode, queryEncode, safeQueryDecode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import { isRecord } from '@/utils/objects';
import type { DeleteObjectsFilter } from '@/services/db/object/mutations';
import { normalizeObjectFilterWire, parseObjectFilterWire, type ObjectFilterWire } from '@/features/dryad';

export const OBJECTS_PER_PAGE = 50;

export function queryableObjectFilters(filters: string | null, objectTypeId: string): string | undefined {
  if (filters == null || isEmptyString(filters)) return undefined;
  try {
    return queryEncode(normalizeObjectFilterWire(lzQueryDecode(filters), objectTypeId));
  } catch {
    return filters;
  }
}

const DeletableObjectFilter = z.custom<DeleteObjectsFilter>(
  (value) =>
    isRecord(value) && ['and', 'or', 'not', 'object_attribute', 'relationship_attribute'].some((key) => key in value),
);

export function deletableObjectFilters(filters: string | null, objectTypeId: string): DeleteObjectsFilter | null {
  const encoded = queryableObjectFilters(filters, objectTypeId);
  if (encoded == null) return null;
  const decoded = parseObjectFilterWire(safeQueryDecode(encoded));
  const parsed = DeletableObjectFilter.safeParse(decoded);
  return parsed.success ? parsed.data : null;
}

const OBJECT_LIST_PARSERS = {
  name: parseAsString.withDefault(''),
  filters: parseAsString,
  page: parseAsInteger.withDefault(1),
  without_relationships: parseAsBoolean.withDefault(false),
  with_relationships: parseAsBoolean.withDefault(false),
};

export function useObjectList(objectTypeId: string) {
  const workspaceId = requireWorkspaceId();
  const queryClient = useQueryClient();
  const [params, setParams] = useQueryStates(OBJECT_LIST_PARSERS, { history: 'replace' });
  const [filterByChoice, setFilterByChoice] = useState<'name' | 'conditions'>(() =>
    params.filters == null ? 'name' : 'conditions',
  );
  const activeFilterBy = params.filters == null ? filterByChoice : 'conditions';
  const selectionKey = `${objectTypeId}:${params.name}:${params.filters ?? ''}:${params.page}:${params.without_relationships}:${params.with_relationships}`;
  const [selection, setSelection] = useState<{
    key: string;
    rows: Record<string, boolean>;
    allMatching: boolean;
  }>({
    key: selectionKey,
    rows: {},
    allMatching: false,
  });

  const queryParams = useMemo(
    () => ({
      objectTypeId,
      page: params.page,
      name: params.filters == null && !isEmptyString(params.name) ? params.name.toLowerCase() : undefined,
      filters: queryableObjectFilters(params.filters, objectTypeId),
      without_relationships: params.without_relationships || undefined,
      with_relationships: params.with_relationships || undefined,
    }),
    [objectTypeId, params.filters, params.name, params.page, params.with_relationships, params.without_relationships],
  );
  const query = useQuery({
    ...getObjectsQueryOptions(workspaceId, queryParams, queryClient),
    placeholderData: keepPreviousData,
    throwOnError: true,
  });
  const objects = query.data?.objects ?? [];
  const total = query.data?.meta.pagination.total ?? 0;
  const activeSelection =
    selection.key === selectionKey ? selection : { key: selectionKey, rows: {}, allMatching: false };
  const selectedCount = objects.filter((object) => activeSelection.rows[object.id] === true).length;
  const allSelected =
    activeSelection.allMatching ||
    (objects.length > 0 && total > 0 && selectedCount === objects.length && selectedCount === total);

  const search = (name: string) => void setParams({ name, page: 1 });
  const commitFilters = useDebounceCallback((serialized: ObjectFilterWire, isEmpty: boolean) => {
    const current = normalizeObjectFilterWire(
      params.filters == null ? { and: [] } : safeQueryDecode(params.filters),
      objectTypeId,
    );
    if (isEqual(serialized, current)) return;
    void setParams({ filters: isEmpty ? null : queryEncode(serialized), page: 1 });
  }, 400);
  const applyFilters = (serialized: ObjectFilterWire, isValid: boolean, isEmpty: boolean) => {
    commitFilters.cancel();
    if (isValid || isEmpty) commitFilters(serialized, isEmpty);
  };
  const activateFilterBy = (next: 'name' | 'conditions') => {
    if (next === activeFilterBy) return;
    commitFilters.cancel();
    setFilterByChoice(next);
    void setParams({ name: '', filters: null, page: 1 });
  };
  const setPage = (page: number) => {
    setSelection({ key: selectionKey, rows: {}, allMatching: false });
    void setParams({ page });
  };
  const setSavedView = (viewId: string | null) =>
    void setParams({
      without_relationships: viewId === 'objects_without_relationships',
      with_relationships: viewId === 'objects_with_relationships',
      page: 1,
    });

  return {
    workspaceId,
    query,
    objects,
    total,
    message: query.data?.meta.message ?? '',
    name: params.name,
    filters: params.filters,
    activeFilterBy,
    page: params.page,
    withoutRelationships: params.without_relationships,
    withRelationships: params.with_relationships,
    deleteFilters: deletableObjectFilters(params.filters, objectTypeId),
    rowSelection: activeSelection.rows,
    allSelected,
    allMatchingSelected: activeSelection.allMatching,
    setRowSelection: (rows: Record<string, boolean>) => {
      const allOnPageSelected = objects.length > 0 && objects.every((object) => rows[object.id] === true);
      setSelection({ key: selectionKey, rows, allMatching: activeSelection.allMatching && allOnPageSelected });
    },
    selectAll: () =>
      setSelection({
        key: selectionKey,
        rows: Object.fromEntries(objects.map((object) => [object.id, true])),
        allMatching: true,
      }),
    clearSelection: () => setSelection({ key: selectionKey, rows: {}, allMatching: false }),
    search,
    applyFilters,
    activateFilterBy,
    setPage,
    setSavedView,
  };
}
